import type {
  ButtonHTMLAttributes,
  InputHTMLAttributes,
  ReactNode,
  SelectHTMLAttributes,
  KeyboardEvent,
} from 'react';
import { forwardRef, useEffect, useRef } from 'react';
import { cx } from '@/lib/cx';
import { Infobulle } from './Infobulle';

// ── Bouton ─────────────────────────────────────────────────────────────────

type VarianteBouton = 'primaire' | 'secondaire' | 'discret' | 'danger';
type TailleBouton = 'sm' | 'md' | 'lg';

const VARIANTES: Record<VarianteBouton, string> = {
  primaire: 'bg-primary text-white hover:bg-primary-dark shadow-card',
  secondaire:
    'bg-surface text-ink border border-line hover:border-line-strong hover:bg-surface-alt',
  discret: 'text-ink-muted hover:text-ink hover:bg-surface-alt',
  danger: 'bg-error text-white hover:brightness-95 shadow-card',
};

const TAILLES: Record<TailleBouton, string> = {
  sm: 'h-8 px-3 text-small gap-1.5',
  md: 'h-10 px-4 text-body gap-2',
  lg: 'h-12 px-6 text-body gap-2',
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variante?: VarianteBouton;
  taille?: TailleBouton;
  chargement?: boolean;
  icone?: ReactNode;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variante = 'primaire',
    taille = 'md',
    chargement,
    icone,
    children,
    className,
    disabled,
    ...reste
  },
  ref,
) {
  return (
    <button
      ref={ref}
      // Un bouton en cours de traitement doit être inerte, sinon un double clic
      // envoie deux fois la même mutation.
      disabled={disabled || chargement}
      aria-busy={chargement || undefined}
      className={cx(
        'inline-flex items-center justify-center rounded-lg font-medium transition-colors',
        'disabled:cursor-not-allowed disabled:opacity-50',
        VARIANTES[variante],
        TAILLES[taille],
        className,
      )}
      {...reste}
    >
      {chargement ? <Spinner /> : icone}
      {children}
    </button>
  );
});

export function Spinner({ className }: { className?: string }) {
  return (
    <svg className={cx('h-4 w-4 animate-spin', className)} viewBox="0 0 24 24" aria-hidden="true">
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="3"
        fill="none"
      />
      <path
        className="opacity-90"
        fill="currentColor"
        d="M12 2a10 10 0 0 1 10 10h-3a7 7 0 0 0-7-7V2z"
      />
    </svg>
  );
}

// ── Carte ──────────────────────────────────────────────────────────────────

export function Card({
  children,
  className,
  titre,
  action,
}: {
  children: ReactNode;
  className?: string;
  titre?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <section className={cx('rounded-card border border-line bg-surface shadow-card', className)}>
      {(titre || action) && (
        <header className="flex items-center justify-between gap-4 border-b border-line px-5 py-3.5">
          {typeof titre === 'string' ? <h2 className="text-h3">{titre}</h2> : titre}
          {action}
        </header>
      )}
      <div className="p-5">{children}</div>
    </section>
  );
}

/**
 * Sens d'un écart, selon qu'il est bon ou mauvais — pas selon qu'il monte ou
 * descend : une hausse de la part d'avis négatifs est défavorable.
 */
export type TonEcart = 'favorable' | 'defavorable' | 'neutre' | 'vigilance';

const TONS_ECART: Record<TonEcart, string> = {
  favorable: 'bg-success-light text-success',
  defavorable: 'bg-error-light text-error',
  neutre: 'bg-surface-sunken text-ink-muted',
  vigilance: 'bg-warning-light text-warning',
};

/** Écart avec une période de comparaison, déjà formaté (« +9,8 % », « −0,08 »). */
export function Ecart({ texte, ton, titre }: { texte: string; ton: TonEcart; titre?: string }) {
  return (
    <span
      title={titre}
      className={cx(
        'inline-flex items-center whitespace-nowrap rounded-full px-2 py-0.5 text-caption font-semibold tabular-nums',
        TONS_ECART[ton],
      )}
    >
      {texte}
    </span>
  );
}

export function StatCard({
  libelle,
  valeur,
  aide,
  icone,
  ecart,
  tendance,
  definition,
}: {
  libelle: string;
  valeur: ReactNode;
  aide?: string;
  icone?: ReactNode;
  /** Écart avec la période de comparaison. */
  ecart?: { texte: string; ton: TonEcart; titre?: string };
  /** Mini-courbe sous la valeur. */
  tendance?: ReactNode;
  /** Définition de l'indicateur, ouverte par un « i » à côté du libellé. */
  definition?: { texte: ReactNode; lien?: string };
}) {
  return (
    <div className="rounded-card border border-line bg-surface p-4 shadow-card">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="flex items-center gap-1.5 text-overline uppercase text-ink-subtle">
            {libelle}
            {definition && (
              <Infobulle libelle={`Définition : ${libelle}`} lien={definition.lien}>
                {definition.texte}
              </Infobulle>
            )}
          </p>
          <p className="mt-1 text-h1 tabular-nums">{valeur}</p>
          {(ecart || aide) && (
            <p className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-caption text-ink-muted">
              {ecart && <Ecart {...ecart} />}
              {aide && <span>{aide}</span>}
            </p>
          )}
        </div>
        {icone && (
          <span className="shrink-0 rounded-lg bg-primary/10 p-2 text-primary">{icone}</span>
        )}
      </div>
      {tendance && <div className="mt-3">{tendance}</div>}
    </div>
  );
}

// ── Badge ──────────────────────────────────────────────────────────────────

type TonBadge = 'neutre' | 'primaire' | 'succes' | 'avertissement' | 'erreur' | 'info';

const TONS: Record<TonBadge, string> = {
  neutre: 'bg-surface-sunken text-ink-muted',
  primaire: 'bg-primary/10 text-primary',
  succes: 'bg-success-light text-success',
  avertissement: 'bg-warning-light text-warning',
  erreur: 'bg-error-light text-error',
  info: 'bg-info-light text-info',
};

export function Badge({ children, ton = 'neutre' }: { children: ReactNode; ton?: TonBadge }) {
  return (
    <span
      className={cx(
        'inline-flex items-center rounded-full px-2 py-0.5 text-caption font-medium',
        TONS[ton],
      )}
    >
      {children}
    </span>
  );
}

// ── Alerte ─────────────────────────────────────────────────────────────────

export function Alert({
  ton = 'info',
  titre,
  children,
  className,
}: {
  className?: string;
  ton?: 'info' | 'succes' | 'avertissement' | 'erreur';
  titre?: string;
  children?: ReactNode;
}) {
  const styles = {
    info: 'border-info/30 bg-info-light text-info',
    succes: 'border-success/30 bg-success-light text-success',
    avertissement: 'border-warning/30 bg-warning-light text-warning',
    erreur: 'border-error/30 bg-error-light text-error',
  }[ton];

  return (
    <div
      role={ton === 'erreur' ? 'alert' : 'status'}
      className={cx('rounded-lg border px-4 py-3 text-small', styles, className)}
    >
      {titre && <p className="font-semibold">{titre}</p>}
      {children && <div className={titre ? 'mt-0.5' : undefined}>{children}</div>}
    </div>
  );
}

// ── Champs de formulaire ───────────────────────────────────────────────────

const CHAMP =
  'w-full rounded-lg border border-line bg-surface px-3 py-2 text-body text-ink placeholder:text-ink-subtle ' +
  'transition-colors focus:border-primary disabled:bg-surface-alt disabled:text-ink-muted';

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  aide?: string;
  erreur?: string;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { label, aide, erreur, id, className, ...reste },
  ref,
) {
  const identifiant = id ?? reste.name;
  return (
    <div className="space-y-1.5">
      {label && (
        <label htmlFor={identifiant} className="block text-small font-medium text-ink">
          {label}
        </label>
      )}
      <input
        ref={ref}
        id={identifiant}
        aria-invalid={erreur ? true : undefined}
        aria-describedby={erreur ? `${identifiant}-erreur` : undefined}
        className={cx(CHAMP, erreur && 'border-error', className)}
        {...reste}
      />
      {erreur ? (
        <p id={`${identifiant}-erreur`} className="text-caption text-error">
          {erreur}
        </p>
      ) : (
        aide && <p className="text-caption text-ink-muted">{aide}</p>
      )}
    </div>
  );
});

export interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  label?: string;
  aide?: string;
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { label, aide, id, className, children, ...reste },
  ref,
) {
  const identifiant = id ?? reste.name;
  return (
    <div className="space-y-1.5">
      {label && (
        <label htmlFor={identifiant} className="block text-small font-medium text-ink">
          {label}
        </label>
      )}
      <select ref={ref} id={identifiant} className={cx(CHAMP, className)} {...reste}>
        {children}
      </select>
      {aide && <p className="text-caption text-ink-muted">{aide}</p>}
    </div>
  );
});

export function Toggle({
  checked,
  onChange,
  label,
  disabled,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label: string;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className="inline-flex items-center gap-2.5 disabled:opacity-50"
    >
      <span
        className={cx(
          'relative h-5 w-9 rounded-full transition-colors',
          checked ? 'bg-primary' : 'bg-line-strong',
        )}
      >
        {/* `left-0` : un bouton centre son texte, et un curseur sans bord gauche
            partirait du milieu de la piste — il paraîtrait toujours activé. */}
        <span
          className={cx(
            'absolute left-0 top-0.5 h-4 w-4 rounded-full bg-white shadow transition-transform',
            checked ? 'translate-x-[1.125rem]' : 'translate-x-0.5',
          )}
        />
      </span>
      <span className="text-small text-ink">{label}</span>
    </button>
  );
}

// ── États vides et chargement ──────────────────────────────────────────────

export function EmptyState({
  titre,
  aide,
  action,
}: {
  titre: string;
  aide?: string;
  action?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 px-6 py-16 text-center">
      <p className="text-h3 text-ink">{titre}</p>
      {aide && <p className="max-w-sm text-small text-ink-muted">{aide}</p>}
      {action && <div className="mt-3">{action}</div>}
    </div>
  );
}

export function Loading({ libelle = 'Chargement…' }: { libelle?: string }) {
  return (
    <div className="flex items-center justify-center gap-2 py-12 text-ink-muted">
      <Spinner />
      <span className="text-small">{libelle}</span>
    </div>
  );
}

/**
 * Onglets de filtrage.
 *
 * Sémantiquement une barre d'onglets (`role="tablist"`) : les lecteurs d'écran
 * annoncent la position et le nombre, et les flèches gauche/droite naviguent.
 */
export function FilterTabs<T extends string>({
  onglets,
  actif,
  onChange,
}: {
  onglets: { cle: T; libelle: string; compte?: number }[];
  actif: T;
  onChange: (cle: T) => void;
}) {
  const surTouche = (e: KeyboardEvent<HTMLDivElement>) => {
    const i = onglets.findIndex((o) => o.cle === actif);
    if (e.key === 'ArrowRight') onChange(onglets[(i + 1) % onglets.length].cle);
    if (e.key === 'ArrowLeft') onChange(onglets[(i - 1 + onglets.length) % onglets.length].cle);
  };

  return (
    <div role="tablist" onKeyDown={surTouche} className="flex gap-1 border-b border-line">
      {onglets.map((o) => {
        const selectionne = o.cle === actif;
        return (
          <button
            key={o.cle}
            type="button"
            role="tab"
            aria-selected={selectionne}
            tabIndex={selectionne ? 0 : -1}
            onClick={() => onChange(o.cle)}
            className={cx(
              'border-b-2 px-4 py-2.5 text-small transition-colors',
              selectionne
                ? 'border-primary font-medium text-ink'
                : 'border-transparent text-ink-muted hover:text-ink',
            )}
          >
            {o.libelle}
            {o.compte !== undefined && (
              <span className="ml-1.5 tabular-nums text-ink-subtle">
                {o.compte.toLocaleString('fr-FR')}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

/**
 * Fenêtre modale.
 *
 * S'appuie sur `<dialog>` natif : la gestion du focus, la touche Échap et le
 * fond bloquant sont assurés par le navigateur, sans piège à focus à écrire.
 */
export function Modal({
  ouvert,
  onFermer,
  titre,
  children,
  actions,
  largeur = 'md',
}: {
  ouvert: boolean;
  onFermer: () => void;
  titre: string;
  children: ReactNode;
  actions?: ReactNode;
  largeur?: 'sm' | 'md' | 'lg';
}) {
  const ref = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (ouvert && !el.open) el.showModal();
    if (!ouvert && el.open) el.close();
  }, [ouvert]);

  const largeurs = {
    sm: 'w-[min(420px,94vw)]',
    md: 'w-[min(560px,94vw)]',
    lg: 'w-[min(820px,94vw)]',
  };

  return (
    <dialog
      ref={ref}
      onClose={onFermer}
      className={cx(
        largeurs[largeur],
        // `!m-auto` : un parent en `space-y-*` imposerait sa marge haute et
        // décentrerait la fenêtre.
        '!m-auto max-w-none rounded-card border-0 bg-surface p-0 text-ink shadow-modal backdrop:bg-ink/40',
      )}
    >
      {ouvert && (
        <div className="flex max-h-[85vh] flex-col">
          <div className="flex items-start justify-between gap-3 border-b border-line px-5 py-4">
            <h2 className="text-h3">{titre}</h2>
            <button
              type="button"
              onClick={onFermer}
              aria-label="Fermer"
              className="rounded-lg p-1 text-ink-muted hover:bg-surface-alt"
            >
              <span aria-hidden="true">&times;</span>
            </button>
          </div>
          <div className="defilement-fin flex-1 overflow-y-auto px-5 py-4">{children}</div>
          {actions && (
            <div className="flex justify-end gap-2 border-t border-line px-5 py-3">{actions}</div>
          )}
        </div>
      )}
    </dialog>
  );
}
